import { useState } from "react";
import axios from "axios";
import { Mail, Instagram, MessageCircle, Send, MapPin } from "lucide-react";
import { IMG, CONTACT } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import { useT, useLang } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = {
  es: [
    "Oncología",
    "Fibromialgia / Fatiga crónica",
    "Ansiedad e insomnio",
    "Sesión individual online",
    "Sesión presencial Barcelona",
    "Grupo / Taller",
    "Otra consulta",
  ],
  ca: [
    "Oncologia",
    "Fibromiàlgia / Fatiga crònica",
    "Ansietat i insomni",
    "Sessió individual online",
    "Sessió presencial Barcelona",
    "Grup / Taller",
    "Una altra consulta",
  ],
};

const Contacto = () => {
  const t = useT();
  const lang = useLang();
  useSeo({
    title: {
      es: "Contacto · Reserva tu Sesión | Método TAO by Marta",
      ca: "Contacte · Reserva la teva Sessió | Mètode TAO by Marta",
    },
    description: {
      es: "Reserva tu primera sesión con Marta Montardit. Formulario, WhatsApp, email e Instagram. Atención online en todo el mundo y presencial en Barcelona.",
      ca: "Reserva la teva primera sessió amb la Marta Montardit. Formulari, WhatsApp, email i Instagram. Atenció online a tot el món i presencial a Barcelona.",
    },
    path: "/contacto",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.form.error"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        interest: form.interest || null,
        message: form.message,
      });
      setSent(true);
      toast.success(t("contact.form.success"));
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("contact.form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ivory">
      <section className="pt-12 md:pt-20 pb-16 md:pb-20" data-testid="page-contacto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("contact.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
              {t("contact.title1")} <span className="italic text-terracotta">{t("contact.title2")}</span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
              {t("contact.lead")}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="watermark-hide-ivory aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
              <img src={IMG.closeUpBW} alt="Marta Montardit" className="w-full h-full object-cover object-[center_30%] scale-105" fetchPriority="high" decoding="async" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* CONTACT INFO */}
          <Reveal className="lg:col-span-4">
            <div className="bg-charcoal text-ivory rounded-3xl p-8 md:p-10 grain">
              <h2 className="font-serif text-3xl md:text-4xl">Hablemos.</h2>
              <p className="mt-4 text-ivory/70 text-[15px] leading-relaxed">
                Elige el canal con el que te sientas más cómoda.
              </p>

              <ul className="mt-10 space-y-7">
                <li>
                  <a
                    href={CONTACT.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp"
                    className="flex items-start gap-4 group"
                  >
                    <span className="w-10 h-10 rounded-full bg-[#25D366]/15 flex items-center justify-center text-[#25D366] flex-shrink-0">
                      <MessageCircle size={18} />
                    </span>
                    <div>
                      <span className="block text-[10px] tracking-[0.3em] uppercase text-ivory/50">WhatsApp</span>
                      <span className="block text-[16px] mt-1 group-hover:text-terracotta transition-colors">{CONTACT.whatsappDisplay}</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="contact-email"
                    className="flex items-start gap-4 group"
                  >
                    <span className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta flex-shrink-0">
                      <Mail size={18} />
                    </span>
                    <div className="min-w-0">
                      <span className="block text-[10px] tracking-[0.3em] uppercase text-ivory/50">Email</span>
                      <span className="block text-[15px] mt-1 group-hover:text-terracotta transition-colors break-all">{CONTACT.email}</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a
                    href={CONTACT.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="flex items-start gap-4 group"
                  >
                    <span className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center text-sage flex-shrink-0">
                      <Instagram size={18} />
                    </span>
                    <div>
                      <span className="block text-[10px] tracking-[0.3em] uppercase text-ivory/50">Instagram</span>
                      <span className="block text-[16px] mt-1 group-hover:text-terracotta transition-colors">@{CONTACT.instagram}</span>
                    </div>
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-10 h-10 rounded-full bg-ivory/10 flex items-center justify-center text-ivory/70 flex-shrink-0">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <span className="block text-[10px] tracking-[0.3em] uppercase text-ivory/50">Modalidades</span>
                    <span className="block text-[15px] mt-1 text-ivory/80">Online · Presencial Barcelona</span>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>

          {/* FORM */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <form
              onSubmit={submit}
              className="bg-ivory border border-sand/70 rounded-3xl p-8 md:p-12 shadow-sm"
              data-testid="contact-form"
            >
              <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-3">Formulario</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
                Cuéntame en qué momento estás.
              </h2>

              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <Field id="name" label={t("contact.form.name") + "*"} value={form.name} onChange={update("name")} testId="form-name" />
                <Field id="email" label={t("contact.form.email") + "*"} type="email" value={form.email} onChange={update("email")} testId="form-email" />
                <Field id="phone" label={t("contact.form.phone")} value={form.phone} onChange={update("phone")} testId="form-phone" />
                <div className="flex flex-col gap-2">
                  <label htmlFor="interest" className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55">{t("contact.form.interest")}</label>
                  <select
                    id="interest"
                    value={form.interest}
                    onChange={update("interest")}
                    data-testid="form-interest"
                    className="bg-transparent border-b border-charcoal/20 py-3 text-[15px] text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                  >
                    <option value="">{lang === "ca" ? "Selecciona una opció" : "Selecciona una opción"}</option>
                    {INTERESTS[lang].map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-7 flex flex-col gap-2">
                <label htmlFor="message" className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55">{t("contact.form.message") + "*"}</label>
                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="form-message"
                  placeholder={lang === "ca" ? "Explica'm breument en quin moment estàs i què necessites..." : "Cuéntame brevemente en qué momento estás y qué necesitas..."}
                  className="bg-transparent border-b border-charcoal/20 py-3 text-[15px] text-charcoal placeholder-charcoal/35 focus:outline-none focus:border-terracotta transition-colors resize-none"
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
                <p className="text-[12px] text-charcoal/55 max-w-md leading-relaxed">
                  {lang === "ca" ? "Les teves dades són confidencials. Et respondré personalment en menys de 48h." : "Tus datos son confidenciales. Te responderé personalmente en menos de 48h."}
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="form-submit"
                  className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-8 py-4 text-sm font-medium hover:bg-terracotta transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? t("contact.form.sending") : sent ? (lang === "ca" ? "Enviat ✓" : "Enviado ✓") : t("contact.form.send")}
                  {!loading && !sent && <Send size={15} />}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const Field = ({ id, label, value, onChange, type = "text", testId }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55">{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="bg-transparent border-b border-charcoal/20 py-3 text-[15px] text-charcoal focus:outline-none focus:border-terracotta transition-colors"
    />
  </div>
);

export default Contacto;
